import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  Typography,
} from "@mui/material";
import {
  isAuthenticated,
  updateSubjectStatus,
} from "../../../ApiService/ApiService";
import { Person, Adjust, Subject } from "@mui/icons-material";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  { id: "name", label: "Name", icon: <Person sx={{ marginRight: 1 }} /> },
  {
    id: "sujectcode",
    label: "Subject Code",
    icon: <Subject sx={{ marginRight: 1 }} />,
  },
  { id: "status", label: "Status", icon: <Adjust sx={{ marginRight: 1 }} /> },
];

function SubjectTable({ subjects, fetchSubjects }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(subjects?.data);
  }, [subjects]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleStatusChange = async (id, currentStatus) => {
    try {
      if (isAuthenticated()) {
        const updatedStatus = !currentStatus;
        const response = await updateSubjectStatus(id, {
          isActive: updatedStatus,
        });
        // console.log("RES", response)
        if (response) {
          if (response?.data?.status === 200) {
            const updatedData = paginatedData.map((subject) =>
              subject.id === id
                ? { ...subject, isActive: updatedStatus }
                : subject
            );
            setDialog({
              visible: true,
              title: "Success",
              message: "Status updated successfully",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setPaginatedData(updatedData);
            fetchSubjects();
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.name}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {row?.code}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row?.isActive}
                          color={row?.isActive ? "success" : "default"}
                          onChange={() =>
                            handleStatusChange(row?.id, row?.isActive)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

SubjectTable.propTypes = {
  subjects: PropTypes.object.isRequired,
  fetchSubjects: PropTypes.func.isRequired,
};

export default SubjectTable;
